import { graphql } from 'gatsby';
import React, { FC, ReactNode, useMemo } from 'react';

import { getOperatorFromEnv } from '@/utils/operators';
import { ContextBuilder } from './ContextBuilder';

export const OperatorFragment = graphql`
  fragment SanityOperator on SanityOperator {
    Elmst
    Elvel
    Hundise
    Infiniza
    Frenwall
    Ludaro
    Apexino
    Fortavo
  }
`;

export type Operator = keyof Queries.SanityOperatorFragment;

export type EnabledOperators = Record<Operator, boolean | null>;

type OperatorContextValue = {
  isEnabled: boolean;
};

const OperatorContext = new ContextBuilder<OperatorContextValue>(
  'OperatorContext',
);

export const isEnabledForOperator = (
  enabledOperators: EnabledOperators | null | undefined,
  fallback = true,
) => {
  if (!enabledOperators) {
    return fallback;
  }

  const currentOperator = getOperatorFromEnv()?.path;

  const normalizedOperators = Object.entries(enabledOperators).reduce(
    (acc, [key, value]) => {
      acc[key.toLowerCase()] = value;
      return acc;
    },
    {} as Record<string, boolean | null>,
  );

  const isOperatorEnabled = normalizedOperators[currentOperator as string];

  return isOperatorEnabled !== null ? Boolean(isOperatorEnabled) : fallback;
};

export const useIsEnabledForOperator = (
  enabledOperators: EnabledOperators | null,
) => {
  const ctx = OperatorContext.use();

  return {
    isEnabled: isEnabledForOperator(enabledOperators, ctx.isEnabled),
  };
};

export const OperatorProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const ctx = useMemo(() => {
    // Default context value will be used if no operator is specified
    return { isEnabled: true };
  }, []);

  return (
    <OperatorContext.Context.Provider value={ctx}>
      {children}
    </OperatorContext.Context.Provider>
  );
};
